/*
 * Copyright (C) 2013-2026 Neckar IT GmbH, Mössingen, Germany
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Linking this library statically or dynamically with other modules is
 * making a combined work based on this library. Thus, the terms and
 * conditions of the GNU General Public License cover the whole combination.
 *
 * As a special exception, the copyright holders of this library give you
 * permission to link this library with independent modules, regardless of
 * the license terms of these independent modules, and to copy and distribute
 * the resulting combined work under terms of your choice, provided that every
 * copy of the combined work is accompanied by a complete copy of the source
 * code of this library.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
package it.neckar.open.version


import it.neckar.open.annotations.CrossLayer
import it.neckar.open.version.io.VersionSerializer
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmOverloads
import kotlin.jvm.JvmStatic

/**
 * A version
 *
 */
@CrossLayer("A version string with the same form in schema metadata, domain and REST views")
@Serializable(with = VersionSerializer::class)
data class Version
/**
 * Creates a version
 *
 * @param major  the major part
 * @param minor  the minor part
 * @param build  the build
 * @param suffix the suffix
 */
@JvmOverloads
constructor(
  /**
   *
   * Getter for the field `major`.
   */
  val major: Int,
  /**
   * Getter for the field `minor`.
   */
  val minor: Int,
  /**
   * Getter for the field `build`.
   */
  val build: Int,
  /**
   * Returns the optional suffix
   *
   * @return the suffix or null if no suffix has been set
   */
  val suffix: String? = null,
) : Comparable<Version> {

  val isSnapshot: Boolean
    get() = suffix != null && suffix.contains("SNAPSHOT")

  init {
    require(major >= 0) { "Invalid major <$major>" }
    require(minor >= 0) { "Invalid minor <$minor>" }
    require(build >= 0) { "Invalid build <$build>" }
  }

  fun withMajor(major: Int): Version {
    return Version(major, minor, build, suffix)
  }

  fun withMinor(minor: Int): Version {
    return Version(major, minor, build, suffix)
  }

  fun withBuild(build: Int): Version {
    return Version(major, minor, build, suffix)
  }

  fun withSuffix(suffix: String?): Version {
    return Version(major, minor, build, suffix)
  }

  /**
   *
   * format
   *
   * @return a String object.
   */
  fun format(): String {
    return if (suffix.isNullOrBlank()) {
      "$major.$minor.$build"
    } else {
      "$major.$minor.$build-$suffix"
    }
  }

  /**
   * {@inheritDoc}
   */
  override fun toString(): String {
    return format()
  }

  /**
   * {@inheritDoc}
   */
  override fun compareTo(other: Version): Int {
    if (major != other.major) {
      return major.compareTo(other.major)
    }

    return if (minor != other.minor) {
      minor.compareTo(other.minor)
    } else {
      build.compareTo(other.build)
    }
  }

  /**
   * Returns an int representation of the version.
   * Only supports [major], [minor] and [build] versions up to 99
   */
  fun toInt(): Int {
    require(major in 0..99) { "Major version must be between 0 and 99" }
    require(minor in 0..99) { "Minor version must be between 0 and 99" }
    require(build in 0..99) { "Build version must be between 0 and 99" }

    return major * 10000 + minor * 100 + build
  }

  /**
   *
   * sameOrSmallerThan
   *
   * @param version a Version object.
   * @return a boolean.
   */
  fun sameOrSmallerThan(version: Version): Boolean {
    return this <= version
  }

  /**
   *
   * smallerThan
   *
   * @param version a Version object.
   * @return a boolean.
   */
  fun smallerThan(version: Version): Boolean {
    return this < version
  }

  /**
   *
   * sameOrGreaterThan
   *
   * @param version a Version object.
   * @return a boolean.
   */
  fun sameOrGreaterThan(version: Version): Boolean {
    return this >= version
  }

  /**
   *
   * greaterThan
   *
   * @param version a Version object.
   * @return a boolean.
   */
  fun greaterThan(version: Version): Boolean {
    return this > version
  }

  /**
   * @param major the major
   * @param minor the minor
   * @param build the build
   * @noinspection ParameterHidesMemberVariable
   */
  fun equals(major: Int, minor: Int, build: Int): Boolean {
    return this.major == major && this.minor == minor && this.build == build
  }

  companion object {

    /**
     * Parses a version
     *
     * @param version the version number as string
     * @return the parsed version
     *
     * @throws IllegalArgumentException if any.
     */
    @JvmStatic
    fun parse(version: String): Version {
      val indexDot0 = version.indexOf('.')
      val indexDot1 = version.indexOf('.', indexDot0 + 2)
      val indexMinus = version.indexOf('-', indexDot1 + 2)

      if (indexDot0 == -1 || indexDot1 == -1) {
        throw IllegalArgumentException("Cannot parse <$version>")
      }

      val major = version.substring(0, indexDot0).toInt()
      val minor = version.substring(indexDot0 + 1, indexDot1).toInt()

      return if (indexMinus == -1) {
        val build = version.substring(indexDot1 + 1).toInt()
        Version(major, minor, build)
      } else {
        val build = version.substring(indexDot1 + 1, indexMinus).toInt()
        val suffix = version.substring(indexMinus + 1)
        Version(major, minor, build, suffix)
      }
    }

    /**
     * Returns the fallback version in case of a parse exception
     */
    @JvmStatic
    fun parseOrFallback(version: String, fallback: Version): Version {
      return try {
        parse(version)
      } catch (_: IllegalArgumentException) {
        fallback
      }
    }

    /**
     * Returns null in case of a parse exception
     */
    @JvmStatic
    fun parseOrNull(version: String): Version? {
      return try {
        parse(version)
      } catch (_: IllegalArgumentException) {
        null
      }
    }

    @JvmStatic
    @Throws(VersionMismatchException::class)
    fun verifyMatch(expected: Version, actual: Version) {
      if (expected != actual) {
        throw VersionMismatchException(expected, actual)
      }
    }

    @JvmStatic
    fun valueOf(major: Int, minor: Int, build: Int): Version {
      return of(major, minor, build)
    }

    @JvmStatic
    fun valueOf(major: Int, minor: Int, build: Int, suffix: String?): Version {
      return of(major, minor, build, suffix)
    }

    @JvmStatic
    fun of(major: Int, minor: Int, build: Int): Version {
      return Version(major, minor, build)
    }

    @JvmStatic
    fun of(major: Int, minor: Int, build: Int, suffix: String?): Version {
      return Version(major, minor, build, suffix)
    }

    @Suppress("ObjectPropertyName")
    @JvmStatic
    val _1_0_0: Version = of(1, 0, 0)

  }
}
